import { useMemo } from "react";
import { useUser } from "../contexts/UserInfoContext";

/**
 * Permission gate for the frontend UI.
 *
 * Source of truth: `userInfo.user_permissions` from `/user-srv/user-info`.
 * Server is still the authoritative enforcer — these checks only hide / show UI.
 *
 * Usage:
 *   const { can, canAny, canAll, isReady } = usePermissions();
 *   if (!isReady) return <Loader />;
 *   {can("campaigns:view") && <CampaignsNav />}
 *
 * isReady === false until `/user-info` has resolved at least once. Gate
 * sensitive UI on it to avoid a flash of "denied" content on first load.
 */
export function usePermissions() {
  const { userInfo, isInitialized } = useUser();

  // Memoize the Set so repeated `can()` calls inside one render are O(1).
  const permissionSet = useMemo(() => {
    return new Set<string>(userInfo?.user_permissions || []);
  }, [userInfo?.user_permissions]);

  const can = (permission: string): boolean => permissionSet.has(permission);

  const canAny = (perms: string[]): boolean =>
    perms.length === 0 ? true : perms.some((p) => permissionSet.has(p));

  const canAll = (perms: string[]): boolean =>
    perms.every((p) => permissionSet.has(p));

  return {
    permissions: userInfo?.user_permissions ?? [],
    can,
    canAny,
    canAll,
    /** True once the user-info fetch has settled (success OR failure). */
    isReady: isInitialized,
  };
}
